import { useEffect, useRef, useCallback } from 'react';
import Plyr from 'plyr';
import Hls from 'hls.js';
import 'plyr/dist/plyr.css';

const VOLUME_STORAGE_KEY = 'video_player_volume';

function getSavedVolume(): number {
  try {
    const saved = localStorage.getItem(VOLUME_STORAGE_KEY);
    if (saved !== null) return parseFloat(saved);
  } catch {
    // localStorage unavailable
  }
  return 1;
}

function saveVolume(vol: number): void {
  try {
    localStorage.setItem(VOLUME_STORAGE_KEY, String(vol));
  } catch {
    // localStorage unavailable
  }
}

interface VideoPlayerProps {
  src: string;
  poster?: string;
  aspectRatio?: '16/9' | '9/16';
  muted?: boolean;
  className?: string;
}

export default function VideoPlayer({
  src,
  poster,
  aspectRatio = '16/9',
  muted = true,
  className,
}: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Plyr | null>(null);

  const plyrRatio = aspectRatio === '9/16' ? '9:16' : '16:9';

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Create a fresh video element each mount so Plyr always gets a clean node
    // (React StrictMode double-mounts cause Plyr.destroy to leave stale DOM)
    const videoEl = document.createElement('video');
    videoEl.playsInline = true;
    videoEl.preload = 'metadata';
    if (poster) videoEl.poster = poster;
    container.appendChild(videoEl);

    let hls: Hls | null = null;
    const isHls = src.endsWith('.m3u8');

    if (isHls) {
      if (Hls.isSupported()) {
        hls = new Hls({ enableWorker: true });
        hls.loadSource(src);
        hls.attachMedia(videoEl);
      } else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
        videoEl.src = src;
      }
    } else {
      videoEl.src = src;
    }

    const player = new Plyr(videoEl, {
      controls: [
        'play-large',
        'play',
        'progress',
        'current-time',
        'mute',
        'volume',
        'fullscreen',
      ],
      hideControls: true,
      resetOnEnd: true,
      clickToPlay: false,
      muted,
      volume: muted ? 0 : getSavedVolume(),
      ratio: plyrRatio,
    });

    playerRef.current = player;

    player.on('volumechange', () => {
      if (!player.muted) {
        saveVolume(player.volume);
      }
    });

    // Auto-pause when scrolled out of view
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && player.playing) {
            player.pause();
          }
        });
      },
      { threshold: 0.25 },
    );
    observer.observe(container);

    return () => {
      observer.disconnect();
      hls?.destroy();
      player.destroy();
      playerRef.current = null;
      // Clean up any leftover DOM that Plyr.destroy may not fully remove
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, [src, poster, muted, plyrRatio]);

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    const player = playerRef.current;
    if (!player) return;
    // Only toggle if clicking the video area, not Plyr controls
    const target = e.target as HTMLElement;
    if (target.closest('.plyr__controls') || target.closest('.plyr__control')) return;
    player.togglePlay();
  }, []);

  return (
    <div
      ref={containerRef}
      onClick={handleContainerClick}
      className={`plyr-wrapper rounded-xl ${className ?? ''}`}
      style={{
        '--plyr-color-main': '#A38BFF',
        '--plyr-video-background': '#000',
        '--plyr-menu-background': '#141821',
        '--plyr-menu-color': '#F5F7FF',
      } as React.CSSProperties}
    />
  );
}
